/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import { HallowABFlag, useMixpanel } from "@packages/ab";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { Collection, TrustedCollection } from "@packages/sdk";
import {
  getIsCollectionAlbumOnlyWithSongs,
  useBranch,
  useRequestMe,
} from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  Button,
  ChallengeProgressBar,
  CheckmarkIcon,
  colors,
  constantSemanticColors,
  cssMediaQueryMinWidthTablet,
  getChallengeStatus,
  getDatesLabel,
  getRGBAFromColor,
  getUsersCountLabel,
  HideFromKids,
  JoinChallengeButton,
  numericPercentages,
  numericPixels,
  PlaceholderIcon,
  PlayIcon,
  radius,
  ShareModal,
  Shuffle2Icon,
  spacing,
  styleDynamics,
  SubscriptionIcon,
  Tag,
  Text,
  useIsDesktopViewport,
  useModal,
  useThemeValues,
  zIndices,
} from "@packages/ui";
import { HallowDataTestId } from "@tools/playwright/global";
import Image from "next/image";
import { useCallback, useMemo } from "react";

const containerStyles: Interpolation = {
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  scrollSnapAlign: "center",
  transition:
    "outline 150ms ease-in, opacity 150ms ease-in, scale 150ms ease-in",
  width: numericPercentages[100],
  outline: `${numericPixels[2]} solid transparent`,
  [cssMediaQueryMinWidthTablet]: {
    outline: `${numericPixels[4]} solid transparent`,
  },
};

const imageContainerStyles: Interpolation = {
  aspectRatio: "4 / 3",
  position: "relative",
  width: numericPercentages[100],
  height: "35vh",
  marginTop: spacing.none,
  [cssMediaQueryMinWidthTablet]: {
    height: "unset",
    marginTop: spacing.xl,
  },
};

const imageStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  background: theme.semanticColors.neutralsMedium,
  objectFit: "cover",
  objectPosition: "center",
  objectRepeat: "no-repeat",
  borderRadius: radius.none,
  [cssMediaQueryMinWidthTablet]: {
    borderRadius: radius.s,
  },
});

const gradientBackgroundImageStyles: Interpolation = {
  alignItems: "center",
  bottom: 0,
  display: "flex",
  flexDirection: "column",
  marginBottom: "-1px",
  paddingLeft: spacing.m,
  paddingRight: spacing.m,
  paddingTop: spacing.xxxl,
  position: "absolute",
  width: numericPercentages[100],
  zIndex: 1,
};

const textTitleStyles: Interpolation = {
  color: constantSemanticColors.onColorHighest,
  overflow: "hidden",
  textAlign: "center",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: numericPercentages[100],
};

const buttonStyles: Interpolation = {
  justifyContent: "space-between",
};

const buttonContainerStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  paddingBottom: spacing.m,
  paddingTop: spacing.m,
  background: colors.black,
  paddingLeft: spacing.l,
  paddingRight: spacing.l,
  [cssMediaQueryMinWidthTablet]: {
    background: colors.transparent,
    paddingLeft: spacing.none,
    paddingRight: spacing.none,
  },
};

const tagContainerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.xxs,
  justifyContent: "center",
};

const tagTextStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsMedium,
  textAlign: "center",
});

const progressBarStyle: Interpolation = {
  position: "absolute",
  top: spacing.m,
  left: "50%",
  transform: "translateX(-50%)",
  padding: `${spacing.none} ${spacing.ms}`,
  height: "32px",
  width: "158px",
  zIndex: zIndices[1],
};

const progressBarSliderStyle: Interpolation = {
  height: spacing.ms,
};

const completedTagStyle: Interpolation = {
  position: "absolute",
  top: spacing.m,
  left: "50%",
  transform: "translateX(-50%)",
  borderRadius: radius.full,
  backgroundColor: constantSemanticColors.fixedDarkenMed,
  backdropFilter: "blur(20px)",
  padding: `${spacing.none} ${spacing.ms}`,
  height: "32px",
  width: "fit-content",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing.s,
  color: constantSemanticColors.onColorHighest,
  zIndex: zIndices[1],
};

const tagStyle: Interpolation = {
  width: "fit-content",
  marginTop: spacing.s,
  display: "flex",
  alignItems: "center",
  gap: spacing.xxs,
  justifyContent: "center",
};

const tagsContainerStyle: Interpolation = {
  display: "flex",
  alignItems: "center",
  gap: spacing.xxs,
  justifyContent: "center",
  [cssMediaQueryMinWidthTablet]: {
    marginTop: spacing.ms,
  },
};

type CollectionImageHeaderProps = {
  collection: TrustedCollection | Collection;
  playAll: (shuffle?: boolean) => Promise<any>;
  playCTA: () => void;
  isLoading: boolean;
};

const ChallengeProgressTag = ({
  collection,
}: {
  collection: Pick<Collection, "progress" | "is_completed">;
}) => {
  const t = useTranslations();
  if (collection.is_completed) {
    return (
      <div css={completedTagStyle}>
        <CheckmarkIcon.SimpleOn
          size={16}
          fill={constantSemanticColors.onColorHighest}
        />
        <Text type="title" size="s">
          {t("herald_dashboard_prize_complete")}
        </Text>
      </div>
    );
  }
  return (
    <ChallengeProgressBar
      progress={collection.progress}
      label={t("general_word_progress")}
      css={progressBarStyle}
      sliderCSS={progressBarSliderStyle}
    />
  );
};

export const CollectionImageHeader = ({
  collection,
  playAll,
  playCTA,
  isLoading,
}: CollectionImageHeaderProps) => {
  const analytics = useAnalytics();
  const isDesktop = useIsDesktopViewport();
  const theme = useThemeValues();

  const shareModal = useModal(ShareModal);
  const branch = useBranch();

  const t = useTranslations();
  const { flags } = useMixpanel();
  const isTargetedWebHalfOffFirstYear =
    flags?.[HallowABFlag.WebHalfOffFirstYear]?.isTargeted;

  const { query: user } = useRequestMe();

  const {
    images: { color_hex, large: imageLarge },
    paid,
    title,
  } = collection;

  const rgbaColor = getRGBAFromColor({
    color: color_hex ?? "#000000",
    format: "object",
  });

  const listenForFree =
    !paid || !!user?.data?.subscription || collection.has_access;
  const watchForFree = listenForFree && collection.has_video;

  const hasJoined = (collection as Collection).has_joined;

  const isAvailable = new Date() > new Date(collection.available_at);

  const isChallenge = collection.type === "challenge";

  const isChallengeFinished = new Date() > new Date(collection.ends_at);

  const challengeStatus = getChallengeStatus(
    collection.available_at,
    collection.ends_at,
  );

  const datesLabel = useMemo(
    () => getDatesLabel(collection.available_at, collection.ends_at, t),
    [collection.available_at, collection.ends_at, t],
  );

  const usersCountLabel = useMemo(
    () => getUsersCountLabel(collection, t),
    [collection, t],
  );

  const handleOnClickCaptureShuffleButton = async () => {
    analytics.track({
      event: HallowAnalyticsEvent.TappedShuffleAll,
    });
    await playAll(true);
  };

  const image = (
    <Image src={imageLarge} alt={title} fill={true} css={imageStyles} />
  );

  const inviteButton = (
    <HideFromKids>
      <Button
        isFullWidth={true}
        onPointerDown={() => {
          analytics.track({
            event: HallowAnalyticsEvent.TappedInviteFriends,
            properties: {},
          });
        }}
        size="l"
        variant={isDesktop ? "primary" : "secondary"}
        onClick={async () =>
          shareModal.show({
            color: color_hex,
            imgSrc: imageLarge,
            title,
            link: await branch.collection({ id: collection.id }),
          })
        }
        data-testid={HallowDataTestId.ContentCTAButton}
      >
        {t("web_share_challenge_invite_button_text")}
      </Button>
    </HideFromKids>
  );

  const playButton = (
    <Button
      endIcon={<span style={{ width: 24 }} />}
      isFullWidth={true}
      onClickCapture={() => playCTA()}
      size="l"
      startIcon={
        listenForFree ? <PlayIcon.Small /> : <SubscriptionIcon.Filled />
      }
      css={buttonStyles}
      variant={isDesktop ? "primary" : "secondary"}
      data-testid={HallowDataTestId.ContentCTAButton}
    >
      {collection.paid &&
      !collection.has_access &&
      !user?.data?.subscription &&
      isTargetedWebHalfOffFirstYear
        ? t("sml_redeem_app_cta", { 0: 50 })
        : t(
            user?.data?.subscription ||
              (collection.paid && collection.has_access)
              ? "chapter_details_play_next_session"
              : watchForFree
                ? "button_watch_for_free"
                : listenForFree
                  ? "button_listen_for_free"
                  : collection?.has_video
                    ? "button_subscribe_to_watch"
                    : "button_subscribe_to_listen",
          )}
    </Button>
  );

  const joinButton = (
    <JoinChallengeButton
      collection={collection}
      isFullWidth={true}
      size="l"
      variant={isDesktop ? "primary" : "secondary"}
      data-testid={HallowDataTestId.ContentCTAButton}
    />
  );

  const renderButton = useCallback(() => {
    if (isLoading) {
      return null;
    }

    if (isChallenge) {
      if (!hasJoined) {
        return joinButton;
      }

      if (collection.is_completed) {
        return inviteButton;
      }
    }

    return isAvailable ? playButton : inviteButton;
  }, [
    isChallenge,
    collection.is_completed,
    isChallengeFinished,
    hasJoined,
    isAvailable,
    playButton,
    inviteButton,
    joinButton,
  ]);

  const isAlbumOnlyWithSongs = getIsCollectionAlbumOnlyWithSongs({
    collection,
  });

  const shuffleButton = (
    <Button
      endIcon={<span style={{ width: 24 }} />}
      isFullWidth={true}
      onClickCapture={handleOnClickCaptureShuffleButton}
      size="l"
      startIcon={<Shuffle2Icon />}
      css={buttonStyles}
      variant={isDesktop ? "neutral" : "lighten"}
    >
      {t("shuffle_all")}
    </Button>
  );

  if (isDesktop)
    return (
      <>
        <Text
          data-testid={HallowDataTestId.CollectionTitle}
          size="m"
          type="headline"
        >
          {title}
        </Text>
        <div css={imageContainerStyles}>
          {image}
          {isChallenge && hasJoined && challengeStatus !== "pending" && (
            <ChallengeProgressTag collection={collection} />
          )}
        </div>
        <div css={buttonContainerStyles}>
          {renderButton()}
          {isAlbumOnlyWithSongs && shuffleButton}
          {isChallenge && !isLoading && (
            <div css={tagsContainerStyle}>
              {usersCountLabel && (
                <div css={tagContainerStyles}>
                  {hasJoined && (
                    <CheckmarkIcon.Inline
                      fill={theme.semanticColors.neutralsMedium}
                    />
                  )}
                  <Text type="detail" size="l" css={tagTextStyles}>
                    {usersCountLabel}
                  </Text>
                </div>
              )}
              {datesLabel && (
                <>
                  {usersCountLabel && (
                    <PlaceholderIcon
                      fill={theme.semanticColors.neutralsMedium}
                      size={8}
                    />
                  )}
                  <Text type="detail" size="l" css={tagTextStyles}>
                    {datesLabel}
                  </Text>
                </>
              )}
            </div>
          )}
        </div>
      </>
    );

  return (
    <div css={containerStyles}>
      <div css={imageContainerStyles}>
        <ChallengeProgressTag collection={collection} />
        <div
          css={[
            gradientBackgroundImageStyles,
            styleDynamics.backgroundGradient({
              red: rgbaColor.r,
              green: rgbaColor.g,
              blue: rgbaColor.b,
              direction: "to bottom",
            }),
          ]}
        >
          <Text size="s" type="headline" css={textTitleStyles}>
            {title}
          </Text>
          {isChallenge && !isLoading && (
            <div css={tagsContainerStyle}>
              {usersCountLabel && (
                <Tag css={tagStyle} variant="lighten">
                  {hasJoined && <CheckmarkIcon.Inline />}
                  {usersCountLabel}
                </Tag>
              )}
              {datesLabel && (
                <Tag css={tagStyle} variant="lighten">
                  {datesLabel}
                </Tag>
              )}
            </div>
          )}
        </div>
        {image}
      </div>
      <div
        css={[
          buttonContainerStyles,
          styleDynamics.backgroundColorFromRGBAObject(rgbaColor),
        ]}
      >
        {renderButton()}
        {isAlbumOnlyWithSongs && shuffleButton}
      </div>
    </div>
  );
};
